import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useCouponEvaluation } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { isV2 } from "../../lib/config";
import { localName, money } from "../../lib/format";
import type { CouponOption, DiscountRule } from "../../lib/types";
import { OverrideDialog } from "../v2/OverrideDialog";

/** What the cashier chose. The amount is only for display: the server recomputes it from the rule. */
export interface CouponSelection {
  rule: DiscountRule;
  amount: number;
  note: string;
  approvalUser: string;
  approvalPin: string;
  /** v2: the one-time manager approval (see OverrideDialog) instead of typed credentials. */
  overrideId?: string;
  approverName?: string;
}

/** Is everything the rule demands filled in (note, manager approval)? */
export function couponReady(sel: CouponSelection | null, isManager: boolean): boolean {
  if (!sel) return true;
  if (sel.rule.requiresNote && sel.note.trim().length < 2) return false;
  if (sel.rule.requiresApproval && !isManager) {
    if (isV2()) return !!sel.overrideId;
    if (!sel.approvalUser.trim() || sel.approvalPin.length < 4) return false;
  }
  return true;
}

export function ruleValueLabel(rule: Pick<DiscountRule, "kind" | "value">, cur: string): string {
  return rule.kind === "percent" ? `${rule.value / 100}%` : `${money(rule.value)} ${cur}`;
}

interface Props {
  guardianId: string | null;
  kidId: string | null;
  passProductId: string;
  addons: Array<{ productId: string; qty: number }>;
  /** post-paid checkout: the visit whose running bill the coupon applies to */
  sessionId?: string;
  disabled?: boolean;
  value: CouponSelection | null;
  onChange: (sel: CouponSelection | null) => void;
}

/** Siblings / 2nd visit / Relatives coupons for the current customer and cart. */
export function CouponPicker({ guardianId, kidId, passProductId, addons, sessionId, disabled, value, onChange }: Props) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { isManager } = useAuth();
  const { data } = useCouponEvaluation({ guardianId, kidId, passProductId, addons, sessionId });
  const options = data?.options ?? [];
  const [asking, setAsking] = useState(false);

  // Keep the selection honest: if the customer/cart changes and the coupon stops applying, drop it.
  useEffect(() => {
    if (!value) return;
    const current = options.find((o) => o.rule.code === value.rule.code);
    if (data && (!current || !current.eligible)) onChange(null);
    else if (current && current.amount !== value.amount) onChange({ ...value, amount: current.amount });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (options.length === 0) return null;
  const cur = t("currency");

  const select = (o: CouponOption) => {
    if (disabled || !o.eligible) return;
    onChange(value?.rule.code === o.rule.code ? null : { rule: o.rule, amount: o.amount, note: "", approvalUser: "", approvalPin: "" });
  };

  return (
    <fieldset className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/40 p-3" disabled={disabled}>
      <legend className="px-1 text-[11px] font-black text-amber-900">🎟️ {t("coupon.title")}</legend>
      {disabled && <p className="text-[11px] font-bold text-gray-500">{t("coupon.notWithFree")}</p>}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {options.map((o) => {
          const on = value?.rule.code === o.rule.code;
          return (
            <button
              key={o.rule.id}
              type="button"
              role="checkbox"
              aria-checked={on}
              aria-disabled={!o.eligible || disabled}
              onClick={() => select(o)}
              className={`rounded-xl border-2 p-2.5 text-start transition ${
                on ? "border-cosmic-purple bg-purple-50" : o.eligible && !disabled ? "border-amber-300 bg-white hover:border-cosmic-purple" : "cursor-not-allowed border-gray-200 bg-gray-50 opacity-70"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-black text-cosmic-navy">{localName(o.rule, locale)}</span>
                <span className="num rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-black text-amber-900">{ruleValueLabel(o.rule, cur)}</span>
              </div>
              <div className="mt-1 text-[10px] font-semibold">
                {o.eligible ? (
                  <span className="text-emerald-700">{t("coupon.save", { amount: money(o.amount) })} {cur}</span>
                ) : (
                  <span className="text-gray-500">{t(`coupon.reason.${o.reason ?? "NOT_VALID_NOW"}`)}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {value && (value.rule.requiresNote || value.rule.requiresApproval) && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {value.rule.requiresNote && (
            <div className="sm:col-span-3">
              <label className="label" htmlFor="cp-note">{t("coupon.note")}</label>
              <input id="cp-note" className="field" value={value.note} maxLength={200} onChange={(e) => onChange({ ...value, note: e.target.value })} />
            </div>
          )}
          {value.rule.requiresApproval &&
            (isManager ? (
              <p className="text-[11px] font-black text-emerald-700 sm:col-span-3">✓ {t("coupon.approvedByYou")}</p>
            ) : isV2() ? (
              <div className="flex flex-wrap items-center gap-2 sm:col-span-3">
                {value.overrideId ? (
                  <p className="text-[11px] font-black text-emerald-700">✓ {t("override.approved", { name: value.approverName ?? "" })}</p>
                ) : (
                  <button type="button" className="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => setAsking(true)}>🔐 {t("coupon.requestApproval")}</button>
                )}
                <OverrideDialog open={asking} action="custom_discount" onClose={() => setAsking(false)} onApproved={(id, name) => { onChange({ ...value, overrideId: id, approverName: name }); setAsking(false); }} />
              </div>
            ) : (
              <>
                <p className="text-[11px] font-black text-amber-900 sm:col-span-3">🔐 {t("coupon.approvalTitle")}</p>
                <div>
                  <label className="label" htmlFor="cp-user">{t("coupon.managerUser")}</label>
                  <input id="cp-user" className="field" autoComplete="off" value={value.approvalUser} onChange={(e) => onChange({ ...value, approvalUser: e.target.value })} />
                </div>
                <div>
                  <label className="label" htmlFor="cp-pin">{t("coupon.managerPin")}</label>
                  <input id="cp-pin" className="field num font-mono" type="password" autoComplete="off" inputMode="numeric" value={value.approvalPin} onChange={(e) => onChange({ ...value, approvalPin: e.target.value })} />
                </div>
              </>
            ))}
        </div>
      )}
    </fieldset>
  );
}
